import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';
import OtpDialog from '../components/qpgeneration/OtpDialog';
import BankPickerModal from '../components/qpgeneration/BankPickerModal';
import SuggestReplacementModal from '../components/qpgeneration/SuggestReplacementModal';
import PaperHistoryModal from '../components/qpgeneration/PaperHistoryModal';
import Swal from 'sweetalert2';

// Quill toolbar — matches the rest of the app's rich editors.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    ['formula', 'link', 'image'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'script', 'formula', 'link', 'image',
];

// ----------------------------------------------------------------------------
// Helpers for navigating / updating the content tree by SlotPath.
// SlotPath examples: "top/q:1",  "sec:0/q:2",  "sec:0/q:2/sub:1"
// ----------------------------------------------------------------------------
const slotPathFor = (sectionIndex, questionIndex, subPath = []) => {
  const secPart = sectionIndex === 'top' ? 'top' : `sec:${sectionIndex}`;
  const qPart = `q:${questionIndex}`;
  const subPart = subPath.map((i) => `sub:${i}`).join('/');
  return [secPart, qPart, subPart].filter(Boolean).join('/');
};

const updateQuestionText = (content, path, newText) => {
  const next = JSON.parse(JSON.stringify(content));
  const parts = path.split('/');
  const secPart = parts[0];
  const qPart = parts[1];
  const subParts = parts.slice(2);

  let arr;
  if (secPart === 'top') {
    arr = next.questions;
  } else {
    const secIndex = parseInt(secPart.split(':')[1], 10);
    arr = next.sections[secIndex].questions;
  }
  let q = arr[parseInt(qPart.split(':')[1], 10)];
  for (const sp of subParts) {
    q = q.subQuestions[parseInt(sp.split(':')[1], 10)];
  }
  q.questionText = newText;
  return next;
};

// ----------------------------------------------------------------------------
// QuestionNode — recursive renderer for a question (and its sub-questions).
// ----------------------------------------------------------------------------
function QuestionNode({
  question, path, depth,
  onChangeText, onPickFromBank, readOnly,
  comments,
  // moderator-mode
  isModerator, onModeratorComment,
  // setter-mode (UNDER_REVISION)
  isSetter, onApplySuggestion, onResolveComment,
}) {
  const ownComments = (comments || []).filter((c) => c.SlotPath === path);
  const isLeaf = !question.subQuestions || question.subQuestions.length === 0;

  return (
    <div className={`qpg-qnode depth-${depth}`}>
      <div className="qpg-qhead">
        <div className="qpg-qnum">
          {question.number ? <strong>Q {question.number}</strong> : <strong>Q</strong>}
          {question.marks ? <span className="qpg-qmarks">{question.marks} marks</span> : null}
        </div>
        <div className="qpg-qmeta">
          {question.co && <span className="qpg-meta-chip">CO: {question.co}</span>}
          {question.unit && <span className="qpg-meta-chip">Unit: {question.unit}</span>}
          {question.bl && <span className="qpg-meta-chip">BL: {question.bl}</span>}
          {question.type && <span className="qpg-meta-chip">{question.type}</span>}
          {isLeaf && !readOnly && onPickFromBank && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              style={{ marginLeft: 8 }}
              onClick={() => onPickFromBank(path, question)}
            >
              Pick from Bank
            </button>
          )}
          {isLeaf && isModerator && onModeratorComment && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              style={{ marginLeft: 8 }}
              onClick={() => onModeratorComment(path, question)}
            >
              Comment / Suggest
            </button>
          )}
        </div>
      </div>

      {isLeaf && (
        <div className="qpg-qbody">
          <ReactQuill
            theme="snow"
            value={question.questionText || ''}
            onChange={(html) => onChangeText(path, html)}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            readOnly={readOnly}
            placeholder="Type the question here..."
          />
        </div>
      )}

      {ownComments.length > 0 && (
        <div className="qpg-comment-list">
          {ownComments.map((c) => (
            <div key={c.CommentID} className={`qpg-comment ${c.IsResolved ? 'resolved' : ''}`}>
              <div className="qpg-comment-head">
                <strong>{c.AuthorName}</strong>
                <span className="qpg-comment-date">{new Date(c.CreatedAt).toLocaleString()}</span>
              </div>
              <div className="qpg-comment-body">{c.CommentText}</div>

              {c.SuggestedQuestionHtml && (
                <div
                  className="qpg-suggestion-box"
                  style={{
                    marginTop: 8,
                    padding: 10,
                    background: '#fffde7',
                    border: '1px solid #fbc02d',
                    borderRadius: 4,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#8d6e00', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    Proposed Replacement
                  </div>
                  <div
                    style={{ fontSize: 14 }}
                    dangerouslySetInnerHTML={{ __html: c.SuggestedQuestionHtml }}
                  />
                  {isSetter && !c.IsResolved && onApplySuggestion && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      style={{ marginTop: 8 }}
                      onClick={() => onApplySuggestion(path, c)}
                    >
                      Apply Suggestion
                    </button>
                  )}
                </div>
              )}

              {isSetter && !c.IsResolved && onResolveComment && (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ marginTop: 8 }}
                  onClick={() => onResolveComment(c.CommentID)}
                >
                  Mark Resolved
                </button>
              )}

              {c.IsResolved && <div className="qpg-comment-resolved">Resolved by {c.ResolverName || 'Setter'}</div>}
            </div>
          ))}
        </div>
      )}

      {question.subQuestions && question.subQuestions.length > 0 && (
        <div className="qpg-subs">
          {question.subQuestions.map((sq, i) => (
            <QuestionNode
              key={sq.id || `${path}/sub:${i}`}
              question={sq}
              path={`${path}/sub:${i}`}
              depth={depth + 1}
              onChangeText={onChangeText}
              onPickFromBank={onPickFromBank}
              readOnly={readOnly}
              comments={comments}
              isModerator={isModerator}
              onModeratorComment={onModeratorComment}
              isSetter={isSetter}
              onApplySuggestion={onApplySuggestion}
              onResolveComment={onResolveComment}
            />
          ))}
        </div>
      )}

      {question.connectorAfter?.type === 'OR' && (
        <div className="qpg-connector">— OR —</div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
function QPGenerationEditor() {
  const { paperId } = useParams();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [content, setContent] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const fileInputRef = useRef(null);

  // Bank picker state
  const [bankOpen, setBankOpen] = useState(false);
  const [bankSlot, setBankSlot] = useState(null); // { co, bl, unit, marks }
  const [bankPath, setBankPath] = useState(null);
  // `bankMode` controls what happens after a pick:
  //   'replace'  — setter replacing own question text
  //   'suggest'  — moderator seeding the SuggestReplacementModal
  const [bankMode, setBankMode] = useState('replace');

  // Moderator suggest/comment modal
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestSlot, setSuggestSlot] = useState(null); // { path, question }
  const [suggestPrefill, setSuggestPrefill] = useState('');

  // Moderator action OTP dialogs
  const [moderateAction, setModerateAction] = useState(null); // 'SEND_BACK' | 'APPROVE' | 'REJECT' | null
  const [moderateNote, setModerateNote] = useState('');

  // History modal
  const [historyOpen, setHistoryOpen] = useState(false);

  // ---------------- Load ----------------
  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const metaRes = await api.get(`/qp-generation/papers/${paperId}`);
      if (!metaRes.data.success) throw new Error('load failed');
      setMeta(metaRes.data.data);

      const method = metaRes.data.data.generationMethod;

      // Load content when applicable. Setter can read DRAFT/UNDER_REVISION content directly.
      if (method !== 'DIRECT_UPLOAD') {
        try {
          const c = await api.get(`/qp-generation/papers/${paperId}/content`);
          if (c.data.success) {
            let loadedContent = c.data.data.content;
            // If Manual and no content yet, seed from template
            if (!loadedContent && method === 'MANUAL_GENERATION') {
              const seedRes = await api.get(`/qp-generation/papers/${paperId}/template-structure`);
              if (seedRes.data.success) loadedContent = seedRes.data.data.structure;
            }
            setContent(loadedContent || { questions: [], sections: [] });
          }
        } catch {
          // If content fetch fails (e.g. no OTP after submit), leave content null
          setContent(null);
        }
      }

      // Comments (visible to everyone on the paper)
      try {
        const cm = await api.get(`/qp-generation/papers/${paperId}/comments`);
        if (cm.data.success) setComments(cm.data.data || []);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load paper');
      navigate('/qp-generation');
    } finally {
      setLoading(false);
    }
  }, [paperId, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ---------------- Edit handlers ----------------
  const handleChangeText = (path, html) => {
    setContent((prev) => updateQuestionText(prev, path, html));
    setDirty(true);
    dirtyRef.current = true;
  };

  const handlePickFromBank = (path, question) => {
    setBankPath(path);
    setBankSlot({
      co: question.co || '',
      bl: question.bl || '',
      unit: question.unit || '',
      marks: question.marks || '',
    });
    setBankMode('replace');
    setBankOpen(true);
  };

  const handleBankPicked = (html) => {
    if (bankMode === 'suggest') {
      // Moderator used Pick-from-Bank inside the Suggest modal — prefill it.
      // Suggest modal stayed mounted (beneath BankPickerModal), so just close bank.
      setSuggestPrefill(html);
      setBankOpen(false);
      setBankMode('replace');
      return;
    }
    if (!bankPath) return;
    setContent((prev) => updateQuestionText(prev, bankPath, html));
    setDirty(true);
    dirtyRef.current = true;
    setBankOpen(false);
    setBankPath(null);
    setBankSlot(null);
    toast.success('Question inserted from bank');
  };

  // ---------------- Moderator actions ----------------
  const handleOpenModeratorComment = (path, question) => {
    setSuggestSlot({ path, question });
    setSuggestPrefill('');
    setSuggestOpen(true);
  };

  const handleOpenBankForSuggestion = () => {
    // Open BankPickerModal in 'suggest' mode — Suggest modal will be reopened
    // with the picked HTML after a pick.
    if (!suggestSlot) return;
    setBankPath(suggestSlot.path);
    setBankSlot({
      co: suggestSlot.question.co || '',
      bl: suggestSlot.question.bl || '',
      unit: suggestSlot.question.unit || '',
      marks: suggestSlot.question.marks || '',
    });
    setBankMode('suggest');
    // Keep SuggestReplacementModal mounted so commentText is preserved.
    // BankPickerModal renders on top via its own backdrop.
    setBankOpen(true);
  };

  const handlePostModeratorComment = async ({ commentText, suggestedQuestionHtml }) => {
    if (!suggestSlot) return;
    try {
      await api.post(`/qp-generation/papers/${paperId}/comments`, {
        slotPath: suggestSlot.path,
        commentText,
        suggestedQuestionHtml,
      });
      toast.success('Comment posted');
      setSuggestOpen(false);
      setSuggestSlot(null);
      setSuggestPrefill('');
      // Refresh comments
      const cm = await api.get(`/qp-generation/papers/${paperId}/comments`);
      if (cm.data.success) setComments(cm.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post comment');
    }
  };

  const openModerateDialog = async (action) => {
    const config = {
      SEND_BACK: {
        title: 'Send paper back for revision?',
        html: 'The paper setter will be notified by email. Please share clear instructions on what to change.',
        inputLabel: 'Moderator note for the Setter (required)',
        inputRequired: true,
        confirmButtonText: 'Send Back',
        confirmButtonColor: '#d97706',
        icon: 'question',
      },
      APPROVE: {
        title: 'Approve this paper?',
        html: 'Approval is final. The paper will be locked as <strong>APPROVED</strong>.',
        inputLabel: 'Optional note',
        inputRequired: false,
        confirmButtonText: 'Approve',
        confirmButtonColor: '#15803d',
        icon: 'success',
      },
      REJECT: {
        title: 'Reject this paper?',
        html: 'Rejection is final. The paper will be locked as <strong>REJECTED</strong>.',
        inputLabel: 'Reason for rejection (required)',
        inputRequired: true,
        confirmButtonText: 'Reject',
        confirmButtonColor: '#b91c1c',
        icon: 'warning',
      },
    }[action];
    if (!config) return;

    const result = await Swal.fire({
      title: config.title,
      html: config.html,
      icon: config.icon,
      input: 'textarea',
      inputLabel: config.inputLabel,
      inputPlaceholder: 'Type your note here...',
      inputAttributes: { 'aria-label': 'Moderator note' },
      showCancelButton: true,
      confirmButtonText: config.confirmButtonText,
      confirmButtonColor: config.confirmButtonColor,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: false,
      preConfirm: (value) => {
        if (config.inputRequired && !String(value || '').trim()) {
          Swal.showValidationMessage('A note is required for this action');
          return false;
        }
        return String(value || '').trim();
      },
    });
    if (!result.isConfirmed) return;
    setModerateNote(result.value || '');
    setModerateAction(action);
  };

  const handleRequestModerateOtp = async () => {
    const res = await api.post(`/qp-generation/papers/${paperId}/request-moderate-otp`, { action: moderateAction });
    return res.data.data || {};
  };

  const handleVerifyModerate = async (otp) => {
    const endpoint = {
      SEND_BACK: 'send-back',
      APPROVE: 'approve',
      REJECT: 'reject',
    }[moderateAction];
    await api.post(`/qp-generation/papers/${paperId}/${endpoint}`, {
      otp,
      note: moderateNote || null,
    });
  };

  const handleModerateDone = () => {
    const msg = {
      SEND_BACK: 'Paper sent back for revision',
      APPROVE: 'Paper approved',
      REJECT: 'Paper rejected',
    }[moderateAction];
    toast.success(msg || 'Done');
    setModerateAction(null);
    setModerateNote('');
    navigate('/qp-generation');
  };

  // ---------------- Setter: apply / resolve ----------------
  const handleApplySuggestion = (path, comment) => {
    if (!comment.SuggestedQuestionHtml) return;
    setContent((prev) => updateQuestionText(prev, path, comment.SuggestedQuestionHtml));
    setDirty(true);
    dirtyRef.current = true;
    toast.success('Suggestion applied — don\'t forget to Save Draft');
  };

  const handleResolveComment = async (commentId) => {
    try {
      await api.put(`/qp-generation/papers/${paperId}/comments/${commentId}`, { isResolved: true });
      const cm = await api.get(`/qp-generation/papers/${paperId}/comments`);
      if (cm.data.success) setComments(cm.data.data || []);
      toast.success('Comment resolved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve');
    }
  };

  const handleSave = async () => {
    if (!meta?.canEdit) return;
    setSaving(true);
    try {
      // Structure snapshot = headers-only view for dashboards
      const snap = {
        sections: (content?.sections || []).map((s) => ({ id: s.id, label: s.label, name: s.name, questionCount: s.questions.length })),
        totalQuestions: (content?.questions?.length || 0)
          + (content?.sections || []).reduce((a, s) => a + s.questions.length, 0),
      };
      await api.put(`/qp-generation/papers/${paperId}/content`, { content, structure: snap });
      toast.success('Saved');
      setDirty(false);
      dirtyRef.current = false;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- Auto-generate ----------------
  const handleAutoGenerate = async () => {
    if (!meta?.canEdit) return;
    if (dirty && !window.confirm('You have unsaved changes. Running auto-generate will overwrite the current content. Continue?')) return;
    setAutoRunning(true);
    try {
      const res = await api.post(`/qp-generation/papers/${paperId}/auto-generate`);
      if (res.data.success) {
        setContent(res.data.data.content);
        setDirty(false);
        dirtyRef.current = false;
        toast.success('Paper auto-generated from Question Bank');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-generate failed');
    } finally {
      setAutoRunning(false);
    }
  };

  // ---------------- File upload (Direct Upload only) ----------------
  const handleFilesChosen = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const form = new FormData();
    for (const f of files) form.append('files', f);

    try {
      setSaving(true);
      await api.post(`/qp-generation/papers/${paperId}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${files.length} file(s)`);
      // Refresh meta so file list updates
      const metaRes = await api.get(`/qp-generation/papers/${paperId}`);
      if (metaRes.data.success) setMeta(metaRes.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setSaving(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/qp-generation/papers/${paperId}/files/${fileId}`);
      toast.success('File deleted');
      const metaRes = await api.get(`/qp-generation/papers/${paperId}`);
      if (metaRes.data.success) setMeta(metaRes.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const res = await api.get(`/qp-generation/papers/${paperId}/files/${fileId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Download failed');
    }
  };

  // ---------------- Submit ----------------
  const handleRequestSubmitOtp = async () => {
    if (dirty) {
      if (!window.confirm('You have unsaved changes. Save before submitting?')) return;
      await handleSave();
    }
    const res = await api.post(`/qp-generation/papers/${paperId}/request-submit-otp`);
    return res.data.data || {};
  };

  const handleVerifySubmit = async (otp) => {
    await api.post(`/qp-generation/papers/${paperId}/submit`, { otp });
  };

  const handleSubmitDone = () => {
    setSubmitOpen(false);
    toast.success('Paper submitted!');
    navigate('/qp-generation');
  };

  // ---------------- Render ----------------
  if (loading) return <div className="page-container"><div className="qpg-empty">Loading...</div></div>;
  if (!meta) return null;

  const isFileMethod = meta.generationMethod === 'DIRECT_UPLOAD';
  const isAutoMethod = meta.generationMethod === 'AUTO_GENERATION';
  const canEdit = meta.canEdit;

  const totalQuestions = (content?.questions?.length || 0)
    + (content?.sections || []).reduce((a, s) => a + (s.questions?.length || 0), 0);

  return (
    <div className="page-container">
      <div className="qpg-editor-header">
        <div>
          <button className="btn btn-link qpg-back-link" onClick={() => navigate('/qp-generation')}>
            ← Back to papers
          </button>
          <h2 style={{ margin: '8px 0 4px' }}>
            {meta.courseCode} — {meta.courseName}
          </h2>
          <div className="qpg-editor-sub">
            {meta.examType} · Set {meta.setName} ·
            {' '}<span className={`qpg-badge qpg-badge-${meta.status.toLowerCase().replace('_', '-')}`}>{meta.status.replace('_', ' ')}</span>
            {' · '}{
              {
                DIRECT_UPLOAD: 'File Upload',
                MANUAL_GENERATION: 'Manual',
                AUTO_GENERATION: 'Auto-Generate',
              }[meta.generationMethod]
            }
          </div>
        </div>
        <div className="qpg-editor-actions">
          {canEdit && !isFileMethod && (
            <button className="btn btn-outline" onClick={handleSave} disabled={saving || !dirty}>
              {saving ? 'Saving...' : (dirty ? 'Save Draft' : 'Saved')}
            </button>
          )}
          {canEdit && isAutoMethod && (
            <button className="btn btn-secondary" onClick={handleAutoGenerate} disabled={autoRunning}>
              {autoRunning ? 'Generating...' : (content && totalQuestions > 0 ? 'Re-run Auto-Generate' : 'Run Auto-Generate')}
            </button>
          )}
          {canEdit && meta.isSetter && (
            <button className="btn btn-primary" onClick={() => setSubmitOpen(true)}>
              Submit Paper
            </button>
          )}
          {(meta.isModerator || meta.isSetter) && (
            <button
              className="btn btn-outline"
              style={{ borderColor: '#546e7a', color: '#546e7a' }}
              onClick={() => setHistoryOpen(true)}
              title="View paper history"
            >
              History
            </button>
          )}
          {meta.isModerator && meta.status === 'SUBMITTED' && (
            <>
              <button
                className="btn btn-outline"
                style={{ borderColor: '#ef6c00', color: '#ef6c00' }}
                onClick={() => openModerateDialog('SEND_BACK')}
              >
                Send Back
              </button>
              <button
                className="btn btn-outline"
                style={{ borderColor: '#c62828', color: '#c62828' }}
                onClick={() => openModerateDialog('REJECT')}
              >
                Reject
              </button>
              <button
                className="btn btn-primary"
                style={{ background: '#2e7d32', borderColor: '#2e7d32' }}
                onClick={() => openModerateDialog('APPROVE')}
              >
                Approve
              </button>
            </>
          )}
        </div>
      </div>

      {!canEdit && meta.isSetter && meta.status !== 'UNDER_REVISION' && (
        <div className="qpg-banner qpg-banner-info">
          This paper is <strong>{meta.status}</strong> and cannot be edited. To view content, request a View OTP from the papers list.
        </div>
      )}

      {meta.isModerator && meta.status === 'SUBMITTED' && (
        <div className="qpg-banner qpg-banner-info">
          You are reviewing this paper as <strong>Moderator</strong>.
          Click <strong>Comment / Suggest</strong> on any question to leave feedback,
          then choose <strong>Send Back</strong>, <strong>Approve</strong>, or <strong>Reject</strong>.
          Each final action requires an OTP sent to your email.
        </div>
      )}

      {meta.moderationNote && (
        <div className="qpg-banner qpg-banner-warn">
          <strong>Moderator note:</strong> {meta.moderationNote}
        </div>
      )}

      {isFileMethod && (
        <div className="qpg-upload-card">
          <h3>Uploaded Files</h3>
          <p className="qpg-hint">PDF or DOCX, max 5 MB each. You can upload multiple files.</p>

          {canEdit && (
            <div className="qpg-upload-zone">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                multiple
                style={{ display: 'none' }}
                onChange={handleFilesChosen}
              />
              <button className="btn btn-primary" onClick={() => fileInputRef.current?.click()} disabled={saving}>
                {saving ? 'Uploading...' : 'Choose Files'}
              </button>
            </div>
          )}

          {(meta.files || []).length === 0 ? (
            <div className="qpg-empty" style={{ padding: 24 }}>No files uploaded yet.</div>
          ) : (
            <table className="qpg-file-list">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {meta.files.map((f) => (
                  <tr key={f.FileID}>
                    <td><strong>{f.FileName}</strong></td>
                    <td>{f.MimeType.includes('pdf') ? 'PDF' : 'DOCX'}</td>
                    <td>{(f.SizeBytes / 1024).toFixed(1)} KB</td>
                    <td>{new Date(f.UploadedAt).toLocaleString()}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => downloadFile(f.FileID, f.FileName)}>Download</button>
                      {canEdit && (
                        <button className="btn btn-outline btn-sm" style={{ marginLeft: 6, color: '#c62828', borderColor: '#c62828' }} onClick={() => handleDeleteFile(f.FileID)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!isFileMethod && (
        <div className="qpg-paper-body">
          {!content || totalQuestions === 0 ? (
            <div className="qpg-empty" style={{ padding: 40 }}>
              {isAutoMethod
                ? <>No content yet. Click <strong>Run Auto-Generate</strong> to populate from the Question Bank.</>
                : <>No questions loaded from the template. (If you just started the paper, try refreshing.)</>}
            </div>
          ) : (
            <>
              {/* Top-level questions (no section) */}
              {(content.questions || []).map((q, i) => (
                <QuestionNode
                  key={q.id || `top/${i}`}
                  question={q}
                  path={slotPathFor('top', i)}
                  depth={0}
                  onChangeText={handleChangeText}
                  onPickFromBank={canEdit && meta.isSetter ? handlePickFromBank : null}
                  readOnly={!canEdit}
                  comments={comments}
                  isModerator={meta.isModerator && meta.status === 'SUBMITTED'}
                  onModeratorComment={handleOpenModeratorComment}
                  isSetter={meta.isSetter && canEdit}
                  onApplySuggestion={handleApplySuggestion}
                  onResolveComment={handleResolveComment}
                />
              ))}

              {(content.sections || []).map((sec, si) => (
                <div key={sec.id || `sec/${si}`} className="qpg-section">
                  <div className="qpg-section-head">
                    <h3>{sec.name || `Section ${sec.label || si + 1}`}</h3>
                    {sec.instructions && <p className="qpg-section-instr">{sec.instructions}</p>}
                  </div>
                  {(sec.questions || []).map((q, qi) => (
                    <QuestionNode
                      key={q.id || `sec/${si}/q/${qi}`}
                      question={q}
                      path={slotPathFor(si, qi)}
                      depth={0}
                      onChangeText={handleChangeText}
                      onPickFromBank={canEdit && meta.isSetter ? handlePickFromBank : null}
                      readOnly={!canEdit}
                      comments={comments}
                      isModerator={meta.isModerator && meta.status === 'SUBMITTED'}
                      onModeratorComment={handleOpenModeratorComment}
                      isSetter={meta.isSetter && canEdit}
                      onApplySuggestion={handleApplySuggestion}
                      onResolveComment={handleResolveComment}
                    />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <OtpDialog
        isOpen={submitOpen}
        title="Submit Paper"
        description="An OTP will be sent to your registered email. Enter it to lock and submit this paper."
        requestFn={handleRequestSubmitOtp}
        verifyFn={handleVerifySubmit}
        onVerified={handleSubmitDone}
        onClose={() => setSubmitOpen(false)}
      />

      <SuggestReplacementModal
        isOpen={suggestOpen}
        onClose={() => { setSuggestOpen(false); setSuggestSlot(null); setSuggestPrefill(''); }}
        slotInfo={suggestSlot}
        initialHtml={suggestPrefill}
        onOpenBank={meta.generationMethod === 'AUTO_GENERATION' ? handleOpenBankForSuggestion : null}
        onSubmit={handlePostModeratorComment}
      />

      {/* BankPickerModal is rendered AFTER SuggestReplacementModal so it paints on
          top when both are open (moderator "Pick from Bank" flow). */}
      <BankPickerModal
        isOpen={bankOpen}
        onClose={() => {
          setBankOpen(false);
          if (bankMode === 'suggest') {
            // Back out of the bank without a pick — suggest modal stayed mounted.
            setBankMode('replace');
          } else {
            setBankPath(null);
            setBankSlot(null);
          }
        }}
        paperId={paperId}
        slot={bankSlot}
        onPick={handleBankPicked}
      />

      <OtpDialog
        isOpen={moderateAction !== null}
        title={
          moderateAction === 'SEND_BACK' ? 'Send Back for Revision'
            : moderateAction === 'APPROVE' ? 'Approve Paper'
              : moderateAction === 'REJECT' ? 'Reject Paper'
                : ''
        }
        description={`An OTP will be sent to your registered email to confirm this action on ${meta.courseCode}.`}
        requestFn={handleRequestModerateOtp}
        verifyFn={handleVerifyModerate}
        onVerified={handleModerateDone}
        onClose={() => { setModerateAction(null); setModerateNote(''); }}
      />

      <PaperHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        paperId={paperId}
      />
    </div>
  );
}

export default QPGenerationEditor;
